/**
 * Pure helper for the Pass 23 admin warning gates.
 *
 * The server throws BadRequestError with a message prefixed by
 * `WARNING_QTY_REDUCTION_BELOW_ORDERED:` or `WARNING_DELETE_WITH_BILLING:`
 * when an admin's mutation needs an explicit acknowledgement. The UI uses
 * this helper to recognise those errors, open a confirmation dialog, and
 * retry with the corresponding ack flag.
 *
 * The protocol is intentionally string-based (rather than a structured
 * field on the error) because the API surface — ApiError — only carries a
 * message string today. Pass 24 or later may upgrade the API to return
 * a structured `warningCode` field; until then, the prefix is the contract.
 */

export type AdminWarningCode =
  | 'QTY_REDUCTION_BELOW_ORDERED'
  | 'DELETE_WITH_BILLING'
  | 'DELETE_WITH_ACTIVE_PIPELINE'
  | 'DELETE_SUBPART_WITH_BATCHES'
  | 'PROJECT_INCOMPLETE';

export interface AdminWarning {
  code: AdminWarningCode;
  /** The original message with the prefix stripped — safe to show to users. */
  message: string;
}

const PREFIXES: ReadonlyArray<{ marker: string; code: AdminWarningCode }> = [
  { marker: 'WARNING_QTY_REDUCTION_BELOW_ORDERED:', code: 'QTY_REDUCTION_BELOW_ORDERED' },
  { marker: 'WARNING_DELETE_WITH_BILLING:', code: 'DELETE_WITH_BILLING' },
  { marker: 'WARNING_DELETE_WITH_ACTIVE_PIPELINE:', code: 'DELETE_WITH_ACTIVE_PIPELINE' },
  { marker: 'WARNING_DELETE_SUBPART_WITH_BATCHES:', code: 'DELETE_SUBPART_WITH_BATCHES' },
  { marker: 'WARNING_PROJECT_INCOMPLETE:', code: 'PROJECT_INCOMPLETE' },
];

/**
 * Inspect an error message; return the structured warning when present,
 * otherwise null.
 */
export function detectAdminWarning(message: string | null | undefined): AdminWarning | null {
  if (!message) return null;
  for (const p of PREFIXES) {
    const i = message.indexOf(p.marker);
    if (i >= 0) {
      const stripped = message.slice(i + p.marker.length).trim();
      return { code: p.code, message: stripped };
    }
  }
  return null;
}
